import { Environment } from '@us/app-config';
import { isNil, isString } from 'lodash';
import { customAlphabet } from 'nanoid';
import { inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ApiErrorResponse } from '@us/web/shared/data-access';

export function getErrorMessage(error: ApiErrorResponse | any, defaultMessage?: string): string {
    console.log(error);
    if (isString(error)) {
        return error;
    }

    if (!isNil(error.error?.error_description)) {
        if (isString(error.error.error_description)) {
            return error.error.error_description;
        }
        return Object.values(error.error.error_description)?.toString();
    }

    if (!isNil(error.message)) {
        return error.message;
    }

    // TODO: fix ERROR Error: NG0203: inject() must be called from an injection context such as a constructor, a factory function, a field initializer, or a function used with `runInInjectionContext`.
    return defaultMessage ?? inject(TranslocoService).translate('message.error');
}

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-';
export const nanoid = customAlphabet(alphabet, 20);
export const getLocalId = (defaultSize = 30) => {
    const generator = customAlphabet('0123456789', defaultSize);
    let str: string;
    do {
        str = generator();
    } while (str.startsWith('0'));
    return str;
};

export function getRandomNumber(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function uniqueId() {
    return `${new Date().valueOf()}${getRandomNumber(10000000000, 99999999999)}`;
}

export function roundToNearestMinute(date = new Date()) {
    const minutes = 1;
    const ms = 1000 * 60 * minutes;
    return new Date(Math.round(date.getTime() / ms) * ms);
}

export function getSearchableText(rawText: string) {
    return rawText
        .normalize('NFD')
        .replace(/[\u0300-\u036f\u2000-\u200f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .trim();
}

export const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/\D/g, '').replace(/^84|^\+84/, '0');
};

/**
 * @deprecated Don't use Firebase anymore, so I don't need to check the carrier
 */
export const isMobiFonePhoneNumber = (phoneNumber: string) => {
    return /^0(70|79|77|76|78|90|93|89)\d{7}$/.test(formatPhoneNumber(phoneNumber));
};

export const getStringValue = (value: any, defaultValue: string | null = null): string | null => {
    if (isNil(value) || value === 'null' || value === 'undefined') {
        return defaultValue;
    }

    if (!isString(value)) {
        return value.toString();
    }

    return value;
};

export const isFnBApp = (env: Environment) => {
    return env.appType === 'fnb';
};

export const getBoolean = (value: number | string | null | undefined, defaultValue = false): boolean => {
    if (isNil(value)) {
        return defaultValue;
    }

    if (typeof value === 'string') {
        const lowercaseValue = value.toLowerCase();
        return lowercaseValue === 'true' || lowercaseValue === '1';
    }

    return Boolean(value);
};

export const getArray = <T>(input: unknown, itemMapper?: (item: unknown) => T | undefined): T[] => {
    const mapped = (array: unknown[]): T[] =>
        (itemMapper ? array.map(itemMapper).filter((item): item is T => item !== undefined) : array) as T[];

    if (Array.isArray(input)) {
        return mapped(input);
    }

    if (isNil(input) || (isString(input) && input.trim() === '')) {
        return [];
    }
    if (isString(input)) {
        try {
            const parsed = JSON.parse(input);
            if (Array.isArray(parsed)) {
                return mapped(parsed);
            }
            // eslint-disable-next-line no-empty
        } catch (error) {}
        return mapped(input.split(','));
    }

    return mapped([input]);
};

export const getObject = <T>(value: unknown): T => {
    if (typeof value === 'object') {
        return value as T;
    }
    if (isNil(value) || !isString(value) || value.trim() === '') {
        return {} as T;
    }
    return JSON.parse(value);
};

export const getNearestAspectRatio = (width: number, height: number): string => {
    const aspectRatio = width / height;

    // Define the available aspect ratios
    const availableRatios = [
        { ratio: 1, label: '1/1' },
        { ratio: 4 / 3, label: '4/3' },
        { ratio: 16 / 9, label: '16/9' },
        { ratio: 21 / 9, label: '21/9' }
    ];

    // Find the closest aspect ratio
    let closestRatio = availableRatios[0];
    let smallestDifference = Math.abs(aspectRatio - closestRatio.ratio);

    for (let i = 1; i < availableRatios.length; i++) {
        const currentRatio = availableRatios[i];
        const currentDifference = Math.abs(aspectRatio - currentRatio.ratio);

        if (currentDifference < smallestDifference) {
            closestRatio = currentRatio;
            smallestDifference = currentDifference;
        }
    }

    return closestRatio.label;
};

export function calculatePercentageReduction(baseAmount: number, percent: number, decimalPlaces = 2) {
    const result = baseAmount * (1 - 1 / (1 + percent / 100));

    // Round the result to the specified number of decimal places
    return Math.round(result * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
}
